import { type ProviderDriverKind, type ProviderOptionDescriptor } from "@amoreg/contracts";
import { getProviderOptionCurrentLabel, getProviderOptionCurrentValue } from "@amoreg/shared/model";

/**
 * Providers whose traits render like T3's Codex picker: Reasoning and Service
 * Tier selects, a bolt when a faster tier is on, a truncating trigger. The
 * Amoreg agents follow the Codex presentation exactly.
 */
export function isCodexStyleProvider(provider: ProviderDriverKind): boolean {
  return provider === "codex" || provider === "amoreg-eve" || provider === "amoreg-inspector";
}

export type TraitsTriggerSpeed = "fast" | "ultrafast";

export interface TraitsTriggerDisplay {
  readonly label: string;
  readonly speed: TraitsTriggerSpeed | null;
  readonly accessibleLabel: string;
}

const SPEED_WORD: Record<TraitsTriggerSpeed, string> = { fast: "Fast", ultrafast: "Ultrafast" };

const SPEED_ACCESSIBLE_SUFFIX: Record<TraitsTriggerSpeed, string> = {
  fast: "Fast mode on",
  ultrafast: "Ultrafast on",
};

function triggerDisplay(label: string, speed: TraitsTriggerSpeed | null): TraitsTriggerDisplay {
  return {
    label,
    speed,
    accessibleLabel: speed ? `${label}, ${SPEED_ACCESSIBLE_SUFFIX[speed]}` : label,
  };
}

export function buildTraitsTriggerDisplay(input: {
  provider: ProviderDriverKind;
  descriptors: ReadonlyArray<ProviderOptionDescriptor>;
  primarySelectDescriptorId: string | null;
  ultrathinkPromptControlled: boolean;
}): TraitsTriggerDisplay {
  let hasFastMode = false;
  let speed: TraitsTriggerSpeed | null = null;
  const labels: Array<string> = [];
  for (const descriptor of input.descriptors) {
    if (descriptor.id === "fastMode" && descriptor.type === "boolean") {
      hasFastMode = true;
      if (descriptor.currentValue === true) speed ??= "fast";
      continue;
    }
    if (
      isCodexStyleProvider(input.provider) &&
      descriptor.id === "serviceTier" &&
      descriptor.type === "select"
    ) {
      const currentValue = getProviderOptionCurrentValue(descriptor);
      const fastTier = descriptor.options.find(({ label }) => label === "Fast");
      if (fastTier && (currentValue === "default" || currentValue === fastTier.id)) {
        hasFastMode = true;
        if (currentValue === fastTier.id) speed ??= "fast";
        continue;
      }
      const ultrafastTier = descriptor.options.find(({ label }) => label === "Ultrafast");
      if (ultrafastTier && currentValue === ultrafastTier.id) {
        hasFastMode = true;
        speed = "ultrafast";
        continue;
      }
    }
    const label =
      input.ultrathinkPromptControlled && descriptor.id === input.primarySelectDescriptorId
        ? "Ultrathink"
        : descriptor.type === "boolean"
          ? `${descriptor.label} ${descriptor.currentValue === true ? "On" : "Off"}`
          : getProviderOptionCurrentLabel(descriptor);
    if (typeof label === "string" && label.length > 0) {
      labels.push(label);
    }
  }

  // Only fall back to text when fast mode is genuinely the sole trait. Keying
  // off an empty label list alone would also catch descriptors that resolved to
  // no label at all, printing a bogus "Normal" for a model without fast mode.
  if (labels.length === 0 && hasFastMode) {
    return triggerDisplay(speed ? SPEED_WORD[speed] : "Normal", null);
  }
  return triggerDisplay(labels.join(" · "), speed);
}
